import {Box, Button, HStack, Text, Tooltip} from '@chakra-ui/react';
import {ContactComponent} from './components/ContactComponent';
import {UserMenu} from './components/UserMenu';
import {ShippingWhiteIcon} from '../../components/svg/ShippingWhiteIcon';
import {CommandeDateTexte} from '@/domains/accueil/components/CommandeDateTexte';
import {useHalState, useHalStates} from '@synako/haligator';
import {useApi, useLivraisons, useMe} from '@/haligator-factory';
import {HEADER_BAND_HEIGHT} from '@/constants/layout';
import {buildSwitchModeUrl, getModeFromUrl} from '@/utils/appMode';
import {useParams} from 'react-router-dom';
import {buildRoute} from '@synako/enhanced-router';
import {routes, useLivraisonsPossiblesContext, usePanier} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {ChevronLeftIcon} from '@chakra-ui/icons';

/**
 * Toggle Vue Commerce / Vue Client du header BO.
 * Bascule le mode applicatif (et le header `x-client-app`) entre commercial et assistance :
 * - « Vue Client » correspond au mode assistance (thème rouge)
 * - « Vue Commerce » au mode commercial (thème bleu).
 * La bascule recharge la page pour réinitialiser halitrek.
 */
const ModeToggle: React.FC = () => {
  const {t} = useTranslation();
  const isAssistance = getModeFromUrl() === 'assistance';
  // Le mode commercial doit être activé pour l'utilisateur pour afficher le toggle de mode dans le header BO
  const modeCommercialEnabled =
    useHalState(useMe()).data?.mode_commercial_enable ?? false;

  const switchTo = (toAssistance: boolean) => {
    return () => {
      if (toAssistance !== isAssistance) {
        window.location.href = buildSwitchModeUrl(toAssistance);
      }
    };
  };

  return (
    <HStack
      bg='blue.deep'
      borderRadius='full'
      p='2px'
      spacing='1'
    >
      {modeCommercialEnabled && (
        <Tooltip label={t('commercial.mode_commercial')}>
          <Button
            variant='unstyled'
            h='28px'
            minW='unset'
            px={{base: '2', md: '3'}}
            borderRadius='full'
            color='white'
            fontSize='sm'
            fontWeight={isAssistance ? '500' : '700'}
            bg={isAssistance ? 'transparent' : 'blue.main'}
            _hover={isAssistance ? {bg: 'whiteAlpha.200'} : {}}
            aria-pressed={!isAssistance}
            aria-label={t('commercial.mode_commercial')}
            onClick={switchTo(false)}
          >
            {/* Libellé masqué en deçà de md : la pastille colorée + le tooltip suffisent */}
            <Text
              as='span'
              display={{base: 'none', md: 'inline'}}
            >
              {t('commercial.mode_commercial')}
            </Text>
          </Button>
        </Tooltip>
      )}
      <Tooltip label={t('commercial.mode_client')}>
        <Button
          variant='unstyled'
          h='28px'
          minW='unset'
          px={{base: '2', md: '3'}}
          borderRadius='full'
          color='white'
          fontSize='sm'
          fontWeight={isAssistance ? '700' : '500'}
          bg={isAssistance ? 'red.assistance' : 'transparent'}
          _hover={isAssistance ? {} : {bg: 'whiteAlpha.200'}}
          aria-pressed={isAssistance}
          aria-label={t('commercial.mode_client')}
          onClick={switchTo(true)}
        >
          {/* Libellé masqué en deçà de md : la pastille colorée + le tooltip suffisent */}
          <Text
            as='span'
            display={{base: 'none', md: 'inline'}}
          >
            {t('commercial.mode_client')}
          </Text>
        </Button>
      </Tooltip>
    </HStack>
  );
};

/**
 * Lien vers un écran du back-office.
 * La navigation se fait par rechargement complet, comme la bascule de mode : le header
 * `x-client-app` envoyé par halitrek doit être réinitialisé en quittant la vue client.
 */
const LienVersLeBackOffice: React.FC<{
  href: string | undefined;
  libelle: string;
}> = ({href, libelle}) => {
  return (
    <Button
      variant='unstyled'
      color='white'
      display='flex'
      alignItems='center'
      fontSize='sm'
      _hover={{textDecoration: 'underline'}}
      onClick={() => {
        if (href) {
          window.location.href = href;
        }
      }}
    >
      <ChevronLeftIcon
        boxSize='20px'
        mr={1}
      />
      <Text fontSize='sm'>{libelle}</Text>
    </Button>
  );
};

const CommercialHeaderContent: React.FC<{client_id: string}> = ({
  client_id,
}) => {
  const client = useApi().follow('get-client', {client_id});
  const societe = client.follow('societe_commerciale');
  const societeState = useHalState(societe);
  const {t} = useTranslation();
  const {date_de_livraison, selectedMarketplaceId} =
    useLivraisonsPossiblesContext();
  const {state: panier} = useHalState(usePanier());
  // Vérification si on est avec un panier PDF
  const isPanierPdf =
    !selectedMarketplaceId &&
    panier?.data.livraison_tolede.date_de_livraison === date_de_livraison &&
    panier.hasOneLink('processus');

  const id_societe_commerciale = societeState.data?.id;
  // Lien vers la fiche client
  const hrefFicheClient = id_societe_commerciale
    ? buildRoute(routes.client.backOfficeSocieteCommercialeClientDetail, {
        id_societe_commerciale,
        id_client: client_id,
      })
    : undefined;
  // Lien vers la liste des imports PDF
  const hrefCommandesPdf =
    isPanierPdf && id_societe_commerciale
      ? buildRoute(routes.client.backOfficeParsePdfOrder, {
          id_societe_commerciale,
        })
      : undefined;

  return (
    <HStack
      position='relative'
      zIndex={1}
      alignItems='center'
      width='100%'
      height='100%'
      px={{base: '10px', md: '20px'}}
      justifyContent='space-between'
    >
      {/* Sorties vers le BO : la fiche client en permanence, et la liste des imports
          en plus quand le panier affiché vient d'une commande PDF. */}
      <HStack
        spacing={{base: '1', lg: '3'}}
        display={{base: 'none', md: 'flex'}}
        flexShrink={0}
      >
        <LienVersLeBackOffice
          href={hrefFicheClient}
          libelle={t('commercial.retour_fiche_client')}
        />
        {hrefCommandesPdf && (
          <>
            <Box
              w='1px'
              h='20px'
              bg='whiteAlpha.400'
            />
            <LienVersLeBackOffice
              href={hrefCommandesPdf}
              libelle={t('commercial.commandes_pdf')}
            />
          </>
        )}
      </HStack>

      {/* Toggle de mode + Nom client. `minW=0` laisse la raison sociale se tronquer
          plutôt que de pousser le bandeau hors de l'écran. */}
      <HStack
        spacing={{base: '2', lg: '4'}}
        minW='0'
      >
        <ModeToggle />
        <Box
          w='1px'
          h='20px'
          bg='whiteAlpha.400'
        />
        <UserMenu />
      </HStack>
    </HStack>
  );
};

export const HeaderBand: React.FC = () => {
  const mode = getModeFromUrl();
  const {client_id} = useParams<{client_id?: string}>();
  // Le bandeau BO (retour fiche client, toggle de mode) s'affiche dans le
  // routing unifié, aussi bien en mode commercial qu'en mode assistance.
  const isBackofficeMode =
    (mode === 'commercial' || mode === 'assistance') && !!client_id;

  const livraisonsPossibles = useHalStates(useLivraisons().followAll('items'));
  const laProchaine = livraisonsPossibles.states?.[0];
  if (!laProchaine && !isBackofficeMode) {
    return;
  }
  return (
    <Box
      position='relative'
      backgroundColor='blue.navy'
      h={HEADER_BAND_HEIGHT}
      w='100%'
    >
      {isBackofficeMode ? (
        <CommercialHeaderContent client_id={client_id} />
      ) : (
        <HStack
          position='absolute'
          top='0'
          left='0'
          alignItems='center'
          justify='center'
          width='100%'
          height='100%'
          pl={{base: '0', md: '4'}}
        >
          <Box display={{base: 'none', md: 'block'}}>
            <ShippingWhiteIcon />
          </Box>
          <Box
            textAlign={{base: 'center', md: 'start'}}
            p={1}
          >
            {laProchaine && (
              <CommandeDateTexte
                date_de_livraison={laProchaine.data.tolede.date_de_livraison}
                color='white'
                fontSize={{base: '2xs', sm: 'xs', md: '2xs', lg: 'sm'}}
              />
            )}
          </Box>
          <HStack
            ml='auto'
            alignItems='center'
            spacing={0}
            display={{base: 'none', md: 'flex'}}
          >
            <ContactComponent />
            <Box
              height='20px'
              borderLeft='1px solid white'
              ml={2}
            />
            <UserMenu />
          </HStack>
        </HStack>
      )}
    </Box>
  );
};
